import { useEffect, useRef, useState } from "react";
import styles from "../ForgottenUsernameScreen/ForgottenUsername.module.scss";
import InputField from "presentation/components/InputField/InputField";
import { Site } from "application/types/Site";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { useTranslation } from "react-i18next";
import { Result } from "application/types/GetLoginDetails";
import api from "application/utilities/axiosIntance";
import { Spinner } from "react-bootstrap";
import DropDown from "presentation/components/DropDown/DropDown";
import Button from "presentation/components/Button/Button";
import { Link, useNavigate } from "react-router-dom";
import { renderLinkOrSpan } from "presentation/components/SupportText/SupportText";
import { isValidEmail } from "application/utilities/validations";
import { SiteType } from "application/types/SiteType";

const ForgottenDetails = () => {

    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [siteListLoaded, setSiteListLoaded] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState("");
    const [siteList, setSiteList] = useState<Site[]>([]);
    const emailRef = useRef<HTMLInputElement | null>(null);
    const region = useSelector((state: RootState) => state.region.region);
    const { t } = useTranslation();

    const layoutState = useSelector((state: RootState) => state.layout) as {
        data: Result;
        isLoading: boolean;
        error: string | null;
    };
    const { data, isLoading } = layoutState;

    useEffect(() => {
        api.get("/Login/GetPortalSiteList?regionCode=" + region)
            .then((response) => {
                const data = response.data;
                if (data.isSuccess && data.result) {
                    setSiteList(data.result);
                    if (data.result.length === 1) {
                        setSelectedProduct(data.result[0].siteType);
                    }
                }
            })
            .catch((error) => {
                console.error(t("GENERIC_SOMETHING_WENT_WRONG"), error);
            })
            .finally(() => {
                setSiteListLoaded(true);
            });
    }, [region, t]);

    useEffect(() => {
        if (selectedProduct && siteListLoaded && emailRef.current) {
            emailRef.current.focus();
        }
    }, [siteListLoaded, selectedProduct]);

    const handleSubmit = () => {
        setErrorMessage("");
        setLoading(true);

        const errors: string[] = [];

        if (!selectedProduct) errors.push(t("FIELD_NAME_PRODUCT"));
        if (!email.trim()) errors.push(t("FIELD_NAME_EMAIL_ADDRESS"));

        if (errors.length > 0) {
            const last = errors.pop();
            const fields = errors.join(", ");
            const message = errors.length
                ? t("FIELD_REQUIRED_MULTIPLE", { fields, last })
                : t("FIELD_REQUIRED_SINGLE", { field: last })

            setErrorMessage(message.charAt(0).toUpperCase() + message.slice(1));
            setLoading(false);
            return;
        }

        if (!isValidEmail(email)) {
            setErrorMessage(t("INVALID_EMAIL"));
            setLoading(false);
            return;
        }


        api
            .post("/Login/ForgottenUserDetails", { regionCode: region, siteType: selectedProduct, email: email })
            .then((response) => {
                const { isSuccess, result } = response.data;
                if (!isSuccess) {
                    setErrorMessage(result?.responseMessage);
                    setLoading(false);
                    return;
                }
                setMessage(result?.responseMessage);
            })
            .catch((error) => {
                console.error("Login error:", error);
                setErrorMessage(t("GENERIC_LOGGING_ERROR"));
                setLoading(false);
            });
    };

    if (isLoading || !siteListLoaded) {
        return <Spinner animation="border" variant="light" />;
    }

    const middleLinks = data?.middleLinks || [];

    return (
        <div className="d-flex justify-content-center align-items-center w-100">
            <nav className={`${styles.popup} px-5 py-4 mb-2`}>
                {!message ?
                    (<>
                        <div className="align-items-center justify-content-center">
                            <div>
                                <div className={`${styles.heading}`}>
                                    {t("FORGOT_DETAILS_HEADING")}
                                </div>
                                <div className={`${styles.description}`}>
                                    {t("FORGOT_DETAILS_INSTRUCTION")}
                                </div>
                            </div>
                        </div>
                        {errorMessage &&
                            <div className={`${styles.error} d-flex align-items-center justify-content-center text-center pt-2 ps-1`}>
                                {errorMessage}
                            </div>
                        }

                        <form onSubmit={(e) => e.preventDefault()}>
                            {siteList.length === 1 ? (
                                <InputField
                                    value={siteList[0].siteName}
                                    readOnly
                                    placeholder="Product"
                                />
                            ) : (
                                <DropDown
                                    options={siteList}
                                    defaultOption="Select a Product"
                                    value={selectedProduct}
                                    onChange={(e) => {
                                        setSelectedProduct(e.target.value);
                                    }}
                                />
                            )}

                            {
                                selectedProduct === SiteType.TRADE_BUYER ? (
                                    <div className={`${styles.helpText} mt-3 text-center `}>
                                        <span>Have you forgotten your </span>
                                        <Link to="/forgottenusername" state={{ fromTBFlow: selectedProduct === SiteType.TRADE_BUYER, product: selectedProduct, email }} className={styles.helpText}>
                                            Username
                                        </Link>
                                        <span> or </span>
                                        <Link to="/forgottenpassword" state={{ fromTBFlow: true, email: email }}
                                            className={styles.helpText}>
                                            Password
                                        </Link>
                                        ?
                                    </div>
                                ) : (
                                    <InputField maxLength={100} value={email} placeholder={t("FIELD_NAME_REGISTEREDEMAIL")}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)} ref={emailRef} />
                                )
                            }
                            {selectedProduct !== SiteType.TRADE_BUYER && (
                                <Button type="submit" label={t("BUTTON_SUBMIT")} onClick={handleSubmit} loading={loading}></Button>
                            )}
                            <Button label={t("BUTTON_CANCEL")} onClick={() => { navigate("/") }}></Button>

                        </form>

                        <div className="mt-3 mb-2">
                            <div className={`justify-content-center mt-2 text-center ${styles.helpText}`}>
                                {middleLinks[1] && renderLinkOrSpan(middleLinks[1], "")}
                                {middleLinks[2] && renderLinkOrSpan(middleLinks[2], styles.details, "", true)}
                            </div>
                        </div>
                    </>)

                    :

                    (<div className="align-items-center justify-content-center text-center">
                        <div className={`${styles.info} d-flex align-items-center justify-content-center pt-2 ps-1 mb-3`}>
                            {message}
                        </div>
                        <Link to="/" className="linkText ps-1">{t("RETURN_MESSAGE")}</Link>
                    </div>)}
            </nav>
        </div >
    )

}

export default ForgottenDetails;